import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertCircle, CheckCircle, Clock, Users, MapPin, Briefcase, Activity } from 'lucide-react';
import { useMmsLookups } from '../../../hooks/useMmsLookups';

/**
 * Job Card Statistics Widget
 * Displays real-time statistics and visualizations for job cards:
 * - Status distribution with color-coded bars
 * - Priority breakdown
 * - Station performance
 * - Assignment metrics
 */
const JobCardStatsWidget = ({ jobCards = [], loading = false, showOverviewCards = true, showPriorityBreakdown = true }) => {
  const { lookups } = useMmsLookups();
  const [stats, setStats] = useState({
    total: 0,
    byStatus: {},
    byPriority: {},
    byStation: {},
    assigned: 0,
    unassigned: 0,
    avgCompletionTime: 0
  });

  useEffect(() => {
    calculateStats();
  }, [jobCards, lookups]);

  const calculateStats = () => {
    if (!jobCards || jobCards.length === 0) {
      setStats({
        total: 0,
        byStatus: {},
        byPriority: {},
        byStation: {},
        assigned: 0,
        unassigned: 0,
        avgCompletionTime: 0
      });
      return;
    }

    const byStatus = {};
    const byPriority = {};
    const byStation = {};
    let assigned = 0;
    let unassigned = 0;
    const completionTimes = [];

    jobCards.forEach(job => {
      // Count by status
      const status = job.OM_STATUS || '0';
      byStatus[status] = (byStatus[status] || 0) + 1;

      // Count by priority - use PRIORITY_NAME directly if available
      const priorityName = job.PRIORITY_NAME || job.PRIORITY || 'Unknown';
      byPriority[priorityName] = (byPriority[priorityName] || 0) + 1;

      // Count by station - use STATION_NAME if available, otherwise lookup by number
      const stationKey = job.STATION_NAME || job.STATION_NO || 0;
      byStation[stationKey] = (byStation[stationKey] || 0) + 1;

      // Count assigned vs unassigned
      // Check if actually assigned (not null, not 0, not empty string)
      const assignedTo = job.ASSIGNEDTO;
      const assignedName = job.ASSIGNED_TO_NAME;
      
      if ((assignedTo && assignedTo !== '0' && assignedTo !== 0) || 
          (assignedName && assignedName.trim() !== '' && assignedName.trim() !== '0')) {
        assigned++;
      } else {
        unassigned++;
      }

      // Calculate completion time for completed jobs
      if (job.COMPLETED_STAMP_DATETIME && job.ISSUED_DATETIME) {
        try {
          const issued = new Date(job.ISSUED_DATETIME);
          const completed = new Date(job.COMPLETED_STAMP_DATETIME);
          const hours = (completed - issued) / (1000 * 60 * 60);
          if (hours > 0 && hours < 8760) { // Less than a year
            completionTimes.push(hours);
          }
        } catch (e) {
          // Invalid date, skip
        }
      }
    });

    const avgCompletionTime = completionTimes.length > 0
      ? completionTimes.reduce((a, b) => a + b, 0) / completionTimes.length
      : 0;

    setStats({
      total: jobCards.length,
      byStatus,
      byPriority,
      byStation,
      assigned,
      unassigned,
      avgCompletionTime
    });
  };

  const getStatusColor = (statusCode) => {
    const colors = {
      '0': { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', bar: 'bg-yellow-500' },
      '1': { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800', bar: 'bg-blue-500' },
      '3': { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800', bar: 'bg-purple-500' },
      '4': { bg: 'bg-indigo-100', border: 'border-indigo-300', text: 'text-indigo-800', bar: 'bg-indigo-500' },
      '5': { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', bar: 'bg-orange-500' },
      '6': { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800', bar: 'bg-green-500' }
    };
    return colors[statusCode] || { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-800', bar: 'bg-gray-500' };
  };

  const getPriorityColor = (priorityRank) => {
    const colors = {
      '0': { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', bar: 'bg-red-500' },
      '1': { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', bar: 'bg-orange-500' },
      '2': { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', bar: 'bg-yellow-500' },
      '3': { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800', bar: 'bg-blue-500' },
      '4': { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-800', bar: 'bg-gray-500' }
    };
    return colors[priorityRank] || { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-800', bar: 'bg-gray-500' };
  };

  const getStatusName = (statusCode) => {
    const status = lookups.om_statuses.find(s => s.OM_STATUS === parseInt(statusCode));
    return status ? status.status_name : `Status ${statusCode}`;
  };

  const getPriorityName = (priorityRank) => {
    const priority = lookups.priorities.find(p => p.PRIORITY_RANK === parseInt(priorityRank));
    return priority ? priority.PRIORITY : 'Unknown';
  };

  const getStationName = (stationNo) => {
    const station = lookups.stations.find(s => s.STATION_NO === parseInt(stationNo));
    return station ? station.NAME : 'Unknown Station';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading statistics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Overview Cards - Conditionally Rendered */}
      {showOverviewCards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Jobs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Briefcase className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Assigned */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.assigned.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats.total > 0 ? ((stats.assigned / stats.total) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Unassigned */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Unassigned</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.unassigned.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats.total > 0 ? ((stats.unassigned / stats.total) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Avg Completion Time */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Completion</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats.avgCompletionTime > 0 ? `${stats.avgCompletionTime.toFixed(1)}h` : 'N/A'}
              </p>
              {stats.avgCompletionTime > 0 && stats.avgCompletionTime < 24 && (
                <p className="text-xs text-green-600 mt-1 flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Within SLA
                </p>
              )}
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Status Distribution and Top Stations - Inline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status Distribution - Progress Bars */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-800 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-blue-600" />
              Status Distribution
            </h3>
            <span className="text-xs text-gray-500">{stats.total} total jobs</span>
          </div>
          <div className="space-y-3">
            {Object.entries(stats.byStatus)
              .sort((a, b) => b[1] - a[1])
              .map(([status, count]) => {
                const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
                const colors = getStatusColor(status);
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{getStatusName(status)}</span>
                      <span className="text-sm text-gray-600">{count} ({percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`${colors.bar} h-2.5 rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            {Object.keys(stats.byStatus).length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No data available</p>
            )}
          </div>
        </div>

        {/* Top Stations - Vertical Bar Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-blue-600" />
            Top Stations
          </h3>
          <div className="flex items-end justify-around h-48 gap-2">
            {Object.entries(stats.byStation)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([stationKey, count]) => {
                const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
                const stationName = isNaN(stationKey) ? stationKey : getStationName(stationKey);
                // Calculate height based on max value
                const maxCount = Math.max(...Object.values(stats.byStation));
                const barHeight = maxCount > 0 ? (count / maxCount) * 100 : 0;
                
                return (
                  <div key={stationKey} className="flex flex-col items-center flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-700 mb-2">{count}</div>
                    <div 
                      className="w-full bg-blue-500 rounded-t-lg transition-all duration-500 hover:bg-blue-600 relative group"
                      style={{ height: `${barHeight}%`, minHeight: '20px' }}
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {percentage.toFixed(1)}%
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 mt-2 text-center truncate w-full" title={stationName}>
                      {stationName.length > 10 ? stationName.substring(0, 10) + '...' : stationName}
                    </div>
                  </div>
                );
              })}
            {Object.keys(stats.byStation).length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4 w-full">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCardStatsWidget;
